import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Sidebar from '../config/sidebar';
import Logo from 'media/images/logo.svg';

import {
  Text,
  Frame,
  Toast,
  Icon,
  TopBar,
  Link,
  Avatar,
  ResourceList,
  BlockStack,
  Box,
  ResourceItem,
  EmptyState,
  SkeletonBodyText,
  Form,
  TextField,
  Button,
  useBreakpoints,
  Badge,
  InlineStack,
} from '@shopify/polaris';
import { ExitIcon, ProfileIcon, SettingsFilledIcon, PersonAddIcon } from '@shopify/polaris-icons';
import helpers from 'helpers/index';
import __ from 'languages/index';
import { useUserLogout } from 'queries/user.query';
import { useAuth } from 'context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useNotification } from 'context/NotificationContext';
import __helpers from 'helpers/index';
import { useSocket } from 'context/SocketContext';
import { useFloatingBanner } from 'context/FloatingBannerProvider';
import { useMutateCreateCustomer, useMutateSearchCustomer } from 'queries/customer.query';
import queryClient from 'queries/index';
import { useMutateSearchUser } from 'queries/users.query';
import { useMutateSearchOrders } from 'queries/orders.query';
import dateandtime from 'date-and-time';

function usePolarisBackdropSync() {
  useEffect(() => {
    const navEl = document.getElementById('AppFrameNav');
    if (!navEl) return;

    const updateBackdropClass = () => {
      // backdrop do Polaris tự render
      const backdrop = document.querySelector('.Polaris-Backdrop.Polaris-Backdrop--belowNavigation') as HTMLElement | null;

      if (!backdrop) return;

      const isVisible = navEl.classList.contains('Polaris-Frame__Navigation--visible');

      // nếu visible => add class showNow, ngược lại remove
      backdrop.classList.toggle('Polaris-Backdrop--belowNavigationShowNow', isVisible);
    };

    // check ngay lần đầu
    updateBackdropClass();

    // quan sát thay đổi class trên nav
    const observer = new MutationObserver(() => {
      updateBackdropClass();
    });

    observer.observe(navEl, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);
}

// rootReducer.js

export default function AppFrame({ children, hasSidebar = true }: { children: any; hasSidebar?: boolean }) {
  const history = useNavigate();

  // let { search } = useLocation();

  /**
   * Check login ...
   */

  const skipToContentRef = useRef(null);
  const { isAuthenticated, user: account } = useAuth();
  const [userMenuActive, setUserMenuActive] = useState(false);
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const toggleUserMenuActive = useCallback(() => setUserMenuActive((_userMenuActive) => !_userMenuActive), []);
  const toggleMobileNavigationActive = useCallback(() => setMobileNavigationActive((_mobileNavigationActive) => !_mobileNavigationActive), []);
  const { mutateAsync: logMeOut, isSuccess: isLogoutSuccess } = useUserLogout();

  useEffect(() => {
    if (isLogoutSuccess) {
      history('/login');
    }
  }, [isLogoutSuccess]);

  const logOutCallback = useCallback(async () => {
    helpers.cookie_delete('access_token');
    helpers.cookie_delete('refresh_token');
    helpers.cookie_delete('expires_at');
    /** xóa app id để không dính lỗi SSO */
    helpers.cookie_set('redirect', window.location.href);
    // load lại để log out ra ngoài ...
    await logMeOut();
  }, []);

  const userMenuMarkup = !helpers.isEmpty(account) ? (
    <TopBar.UserMenu
      customWidth="250px"
      actions={[
        {
          items: [
            { content: __('my_profile'), url: '/profile', icon: ProfileIcon },
            { content: __('edit_my_profile'), url: '/edit-my-profile', icon: SettingsFilledIcon },
          ],
        },
        {
          items: [{ content: __('logout'), icon: ExitIcon, onAction: () => logOutCallback() }],
        },
      ]}
      name={account?.display_name || account?.user_email || account?.user_login}
      detail={account?.user_email}
      initials={String(account?.display_name || account?.user_email || account?.user_login || 'unknown').charAt(0)}
      open={userMenuActive}
      avatar={account.user_avatar ? __helpers.getMediaLink(account.user_avatar) : null}
      onToggle={toggleUserMenuActive}
    />
  ) : (
    <TopBar.UserMenu
      actions={[
        {
          items: [
            { content: __('login'), url: '/login', icon: ExitIcon },
            { content: __('register'), url: '/register', icon: ProfileIcon },
          ],
        },
      ]}
      name={__('welcome')}
      detail={__('customer')}
      initials="U"
      open={userMenuActive}
      onToggle={toggleUserMenuActive}
    />
  );

  /** Tìm kiếm */
  const { addNotification } = useNotification();
  const [searchActive, setSearchActive] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [customerName, setCustomerName] = useState('');
  const { mutateAsync: createCustomer, isPending: isCreatingCustomer, isSuccess: isCreateCustomerSuccess } = useMutateCreateCustomer();

  useEffect(() => {
    if (isCreateCustomerSuccess) {
      setCustomerName('');
      setSearchValue('');
      setSearchActive(false);
    }
  }, [isCreateCustomerSuccess]);

  const createNewCustomerCallback = useCallback(async () => {
    if (!customerName.trim()) return;
    try {
      let newCustomerData = await createCustomer({
        user_phonenumber: helpers.normalizePhoneNumber(searchValue),
        display_name: customerName,
      });
      addNotification('info', 'Khách hàng đã được tạo thành công');
      history(`/customer/view/${newCustomerData.user_id}`);
    } catch (error) {
      console.error(error);
    }
  }, [customerName, searchValue, createCustomer]);

  const {
    mutateAsync: searchCustomer,
    reset: resetSearchingCustomer,
    data: searchCustomerData,
    isPending: searchingCustomer,
  } = useMutateSearchCustomer();
  const { mutateAsync: searchUser, reset: resetSearchingUser, data: searchUserData, isPending: searchingUser } = useMutateSearchUser();
  const { mutateAsync: searchOrder, reset: resetSearchingOrder, data: orderSearchData, isPending: searchingOrder } = useMutateSearchOrders();

  const handleSearchResultsDismiss = useCallback(() => {
    setSearchActive(false);
    setSearchValue('');
  }, []);

  const handleSearchFieldChange = useCallback((value: string) => {
    setSearchValue(value);
    setSearchActive(value.length > 0);

    /** reset toàn bộ dữ liệu */
    resetSearchingCustomer();
    resetSearchingUser();
    resetSearchingOrder();
  }, []);

  const getSearchCallback = useCallback(async (searchValue: string) => {
    searchCustomer({
      query: helpers.normalizePhoneNumber(searchValue),
      limit: 2,
    });
    searchUser({
      query: helpers.normalizePhoneNumber(searchValue),
      user_role: '!customer',
      limit: 2,
    });
    searchOrder({
      query: searchValue,
      limit: 2,
    });
  }, []);

  const searchDebound = useMemo(() => helpers.debounce((_value) => getSearchCallback?.call(this, _value), 500), []);

  useEffect(() => {
    if (!searchValue) {
      return;
    }
    // Do search here
    searchDebound(searchValue);
    // Khi người dùng nhập từ khóa mới -> reset tên khách hàng
    setCustomerName('');
  }, [searchValue]);

  const emptyStateMarkup = useMemo(() => {
    const isPhoneNumber = helpers.isPhoneNumber(searchValue);
    if (searchCustomerData && !searchCustomerData.length && searchUserData && !searchUserData.length) {
      if (isPhoneNumber) {
        return (
          <Box padding="400">
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                Tạo khách hàng mới
              </Text>
              <Form onSubmit={createNewCustomerCallback}>
                <BlockStack gap="400">
                  <TextField label="Số điện thoại" labelHidden value={helpers.normalizePhoneNumber(searchValue)} readOnly autoComplete="off" />
                  <TextField
                    autoFocus
                    label="Tên khách hàng"
                    labelHidden
                    placeholder="Tên khách hàng"
                    value={customerName}
                    onChange={(t) => setCustomerName(helpers.capitalizeWords(t))}
                    autoComplete="off"
                  />
                  <Button submit variant="primary" icon={PersonAddIcon} disabled={!customerName.trim()} loading={isCreatingCustomer}>
                    Tạo khách hàng
                  </Button>
                </BlockStack>
              </Form>
            </BlockStack>
          </Box>
        );
      }
      return (
        <EmptyState heading="Không thấy kết quả nào" image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png">
          <p>Kiểm tra lại mã vé hoặc số điện thoại của khách.</p>
        </EmptyState>
      );
    }
    return null;
  }, [searchCustomerData, customerName, searchValue, isCreatingCustomer, searchUserData]);

  const searchResultsMarkup = (
    <Box width="100%" padding={'0'}>
      {searchingCustomer || searchingOrder || searchingUser ? (
        <Box width="100%" padding="400">
          <SkeletonBodyText />
        </Box>
      ) : null}
      {!searchingCustomer && searchCustomerData && searchCustomerData?.length < 1 && orderSearchData?.length < 1 && emptyStateMarkup}

      <BlockStack gap={'200'}>
        {searchCustomerData && searchCustomerData.length > 0 && (
          <ResourceList
            headerContent="Khách hàng"
            showHeader
            resourceName={{ singular: 'customer', plural: 'customers' }}
            items={searchCustomerData || []}
            loading={searchingCustomer}
            emptyState={emptyStateMarkup}
            renderItem={(customer) => {
              const { display_name, user_phonenumber, user_id, is_vip } = customer;
              const media = <Avatar size="md" name={display_name} initials={display_name.charAt(0)} />;

              return (
                <ResourceItem
                  id={user_id}
                  url={`/customer/view/${user_id}`}
                  onClick={() => setSearchActive(false)}
                  media={media}
                  accessibilityLabel={`View details for ${display_name}`}
                >
                  <Text variant="bodyMd" fontWeight="bold" as="h3">
                    {display_name}
                  </Text>
                  <div>{user_phonenumber}</div>
                </ResourceItem>
              );
            }}
          />
        )}
        {searchUserData && searchUserData.length > 0 && (
          <ResourceList
            headerContent="Nhân viên"
            showHeader
            resourceName={{ singular: 'customer', plural: 'customers' }}
            items={searchUserData || []}
            loading={searchingUser}
            emptyState={emptyStateMarkup}
            renderItem={(userData) => {
              const { display_name, user_phonenumber, user_id } = userData;
              const media = <Avatar size="md" name={display_name} initials={display_name.charAt(0)} />;

              return (
                <ResourceItem
                  id={user_id}
                  url={`/user/view/${user_id}`}
                  onClick={() => setSearchActive(false)}
                  media={media}
                  accessibilityLabel={`View details for ${display_name}`}
                >
                  <Text variant="bodyMd" fontWeight="bold" as="h3">
                    {display_name}
                  </Text>
                  <div>{user_phonenumber}</div>
                </ResourceItem>
              );
            }}
          />
        )}
        {orderSearchData?.length > 0 && (
          <ResourceList
            headerContent={`Tìm thấy ${orderSearchData?.length || 0} đơn hàng`}
            showHeader
            resourceName={{ singular: 'Đơn hàng', plural: 'Đơn hàng' }}
            items={orderSearchData || []}
            loading={searchingOrder}
            renderItem={(order) => {
              const { order_code, id, payment_status, is_exported_invoice, total_must_pay, customer_data, total_seat, departure_date, journey } =
                order;

              return (
                <ResourceItem
                  id={id}
                  onClick={() => {
                    setSearchActive(false);
                    window.location.replace(`/orders/view/${id}`);
                  }}
                  media={null}
                  accessibilityLabel={`View details for ${id}`}
                >
                  <InlineStack gap={'400'} align="start" blockAlign="center">
                    <Text variant="bodyMd" fontWeight="bold" as="h3">
                      {order_code}{' '}
                    </Text>

                    <Badge tone={payment_status === 1 ? 'success-strong' : 'attention-strong'}>
                      {payment_status === 1 ? 'Đã thanh toán' : 'Chưa thanh toán'}
                    </Badge>
                    <Badge tone={is_exported_invoice === 1 ? 'success-strong' : 'attention-strong'}>
                      {is_exported_invoice === 1 ? 'Đã xuất hóa đơn' : 'Chưa xuất hóa đơn'}
                    </Badge>
                  </InlineStack>
                  <div>
                    <Text as="p">
                      Hành khách{' '}
                      <strong>
                        {customer_data?.display_name} / {customer_data?.user_phonenumber}
                      </strong>
                    </Text>
                    <Text as="p">
                      {total_seat} chỗ - {__helpers.formatNumber(total_must_pay) + ' VND'}
                    </Text>
                    <Text as="p">
                      {dateandtime.format(new Date(Number(departure_date)), 'HH:mm DD/MM/YYYY')} - {journey?.journey_from} đi {journey?.journey_to}
                    </Text>
                  </div>
                </ResourceItem>
              );
            }}
          />
        )}
      </BlockStack>
    </Box>
  );

  const searchFieldMarkup = (
    <TopBar.SearchField onChange={handleSearchFieldChange} value={searchValue} placeholder="Tìm theo mã vé hoặc số điện thoại" />
  );

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      userMenu={userMenuMarkup}
      searchResultsVisible={searchActive}
      searchField={isAuthenticated && searchFieldMarkup}
      searchResults={searchResultsMarkup}
      onSearchResultsDismiss={handleSearchResultsDismiss}
      onNavigationToggle={toggleMobileNavigationActive}
      secondaryMenu={[]}
    />
  );

  const logo = {
    width: 150,
    topBarSource: Logo,
    url: '/',
    accessibilityLabel: 'nBee',
  };

  /** Global notification */
  const { notification, clearNotification } = useNotification();

  /** SOCKET  */
  const { socket } = useSocket(); // Lấy socket từ context
  useEffect(() => {
    if (socket) {
      socket.on('change_trip_manifest', ({ trip_manifest_id = null, ticket_id = null }) => {
        // let params = helpers.ExtractUrl(window.location.search);
        // console.info('Change rooif ....: ', trip_manifest_id, ticket_id);
        queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        // queryClient.invalidateQueries({ queryKey: ['orders/fetch_list_order_in_manifest'] });
        // queryClient.invalidateQueries({ queryKey: ['orders/fetch_list_order_log_in_manifest'] });
        queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
        // queryClient.invalidateQueries({ queryKey: ['trip_manifest_fee/fetch_entity_list'] });
        queryClient.invalidateQueries({ queryKey: ['ticket/fetch_unassigned_list'] }); // reset lại cái danh sách vé chưa điều chuyến
        // queryClient.invalidateQueries({ queryKey: ['trip_manifest_ticket/fetch_entity_list'] }); // reset lại cái danh sách vé để hiển thị
      });

      socket.on('receive_transaction', ({ amount = 0, payment_method = 'CASH', order_id = '', description = '' }) => {
        queryClient.invalidateQueries({ queryKey: ['transactions/fetch_entity_list'] }); // reset lại cái danh sách giao dịch
        showBanner({
          title: `+${helpers.formatNumber(amount)} VND`,
          content: (
            <BlockStack gap={'100'}>
              <Text as="p">
                Giao dịch +{helpers.formatNumber(amount)} ({payment_method}) - ID đơn: {order_id || 'Không có'}
              </Text>
              <Text as="p" tone="subdued" variant="bodyXs">
                {description || '-'}
              </Text>
            </BlockStack>
          ),
          duration: 15000,
        });
      });
    }

    return () => {
      if (socket) {
        socket.off('calling'); // Hủy lắng nghe khi unmount
        socket.off('change_trip_manifest'); // Hủy lắng nghe khi unmount
        socket.off('receive_transaction'); // Hủy lắng nghe khi unmount
      }
    };
  }, [socket]);

  // const sendMessage = () => {
  //   if (socket && message.trim()) {
  //     socket.emit("messageFromClient", message); // Gửi tin nhắn tới server
  //     setMessage(""); // Reset input
  //   }
  // };
  const { showBanner } = useFloatingBanner();

  /** SOCKET  */

  /** Thông báo lần đầu khi vào app */
  useEffect(() => {
    setTimeout(() => {
      showBanner({
        title: 'Thông báo',
        content: <p>Một số thông báo quan trọng sẽ có mặt tại đây. Có những thông báo như cuộc gọi tới, có vé mới chờ xử lý ...</p>,
        duration: 8000,
      });
    }, 1000);
  }, []);

  const { mdUp } = useBreakpoints();

  return (
    <>
      <Frame
        topBar={topBarMarkup}
        logo={logo}
        navigation={hasSidebar && isAuthenticated && <Sidebar />}
        showMobileNavigation={mobileNavigationActive}
        onNavigationDismiss={toggleMobileNavigationActive}
        skipToContentTarget={skipToContentRef.current}
      >
        {usePolarisBackdropSync()}
        {mdUp ? <div className="Polaris-Backdrop Polaris-Backdrop--belowNavigation" onClick={() => setMobileNavigationActive(false)}></div> : null}

        {notification && (
          <Toast
            content={notification.message}
            duration={5000}
            onDismiss={() => clearNotification(notification.id)}
            error={notification.type === 'error'}
          />
        )}
        {children}
      </Frame>
    </>
  );
}

